import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "context/NotificationContext";
import { Lang, modelOf, notificationText, timeAgoText } from "utils/notificationText";
import { recordRoute } from "utils/recordRoutes";

/**
 * The bell's list: every unread notification, newest first. It used to keep
 * only discipline complaint notifications (copied from that app), so the
 * count showed but the list said "No new notifications". Opening one marks
 * it read and goes to its record when there is a page for it; ✕ marks it
 * read without leaving the page.
 */




// The list closes when the pointer leaves it (after a short grace so moving
// between the bell and the list does not flicker), when it is opened but not
// pointed at, on a click outside it or Escape, and after opening an item.
const LEAVE_GRACE_MS = 400;
const UNTOUCHED_MS = 4000;

const PopupNotification: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = (["en", "am", "or"].includes(i18n.language?.slice(0, 2))
    ? i18n.language.slice(0, 2)
    : "en") as Lang;
  const { notifications, markAsRead } = useNotifications();
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number>();

  const close = () => onClose?.();
  const closeSoon = (ms: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(close, ms);
  };
  const stay = () => window.clearTimeout(timer.current);

  useEffect(() => {
    closeSoon(UNTOUCHED_MS);
    const outside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (boxRef.current?.contains(target) || target.closest?.("[data-notification-bell]")) return;
      close();
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      window.clearTimeout(timer.current);
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hover = {
    ref: boxRef,
    onMouseEnter: stay,
    onMouseLeave: () => closeSoon(LEAVE_GRACE_MS),
  };

  // The latest notifications, read or not, newest first.
  const LATEST = 6;
  const latest = [...notifications]
    .sort(
      (a: any, b: any) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )
    .slice(0, LATEST);
  const unreadCount = notifications.filter((n: any) => !n.is_read).length;

  // Title and sentence in the selected language (see utils/notificationText).
  const text = (n: any) => notificationText(n, lang, t);

  const open = (n: any) => {
    if (!n.is_read) markAsRead(n.id);
    close();
    const route = recordRoute(modelOf(n), n.object_id);
    if (route) navigate(route[0], route[1] ? { state: route[1] } : undefined);
  };

  const markAllRead = () =>
    notifications.filter((n: any) => !n.is_read).forEach((n: any) => markAsRead(n.id));

  return (
    <div
      {...hover}
      className="fixed top-20 right-4 sm:right-10 z-50 w-[calc(100vw-2rem)] sm:w-96 bg-white border shadow-xl rounded-xl max-h-[80vh] flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <p className="text-sm font-semibold text-gray-900">
          {t("notifications", { defaultValue: "Notifications" })}
        </p>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="text-xs font-medium text-red-600 hover:underline"
          >
            {t("mark_all_as_read", { defaultValue: "Mark all as read" })}
          </button>
        )}
      </div>

      {latest.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-gray-500">
          {t("no_notifications", { defaultValue: "No notifications yet" })}
        </p>
      ) : (
        <div className="space-y-2 overflow-y-auto p-3">
          {latest.map((n: any) => (
            <div
              key={n.id}
              className={`relative rounded-md border p-3 cursor-pointer transition ${
                n.is_read
                  ? "border-gray-100 bg-white hover:bg-gray-50"
                  : "border-red-100 bg-red-50/40 hover:bg-red-50"
              }`}
              onClick={() => open(n)}
            >
              <div className="flex items-start justify-between gap-2">
                {!n.is_read && (
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${n.is_read ? "font-medium text-gray-600" : "font-semibold text-gray-900"}`}>
                    {text(n).title ||
                      t("new_notification", { defaultValue: "New notification" })}
                  </p>
                  <p className={`text-sm mt-1 break-words ${n.is_read ? "text-gray-500" : "text-gray-700"}`}>
                    {text(n).message}
                  </p>
                  {n.timestamp && (
                    <p className="text-xs text-gray-400 mt-1">{timeAgoText(n.timestamp, lang)}</p>
                  )}
                </div>
                {!n.is_read && (
                  <button
                    type="button"
                    aria-label={t("mark_as_read", { defaultValue: "Mark as read" })}
                    title={t("mark_as_read", { defaultValue: "Mark as read" })}
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(n.id);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PopupNotification;
